import * as React from "react";
import queryString from "query-string";
import { useHistory, useLocation } from "react-router-dom";
import { Button } from "../../bits/Button/Button";
import ChevronBackIcon from "../../icons/ChevronBack";
import ClientInfo from "../ClientInfo/ClientInfo";
import {
    ChartLockStatus,
    ClientStatus,
    IClient,
    RtdClientChangesNotification,
} from "intake_shared/src/types";
import store from "../../../../store";
import RefreshIcon from "../../icons/Refresh";
import EditClient from "../client-forms/edit-client/EditClient/EditClient";
import { useToasts } from "react-toast-notifications";
import moment from "../../../../utils/app_moment";
import { AclContext } from "../../../../contexts/AclContext";
import { getClientChangesNotificationRef } from "../../../../realtimedatabase/notification";
import { onChildAdded } from "firebase/database";
import { uniqBy } from "lodash";
import styles from "./ClientStatusView.module.scss";
import { AlertDialog } from "../../bits/AlertDialog/AlertDialog";
import ClientChangeHistoryNotification from "../ClientChangeHistoryNotification/ClientChangeHistoryNotification";
import HistoryIcon from "../../icons/History";
import { useChartLock } from "../../../../contexts/ChartLockContext";
import { LockIcon } from "../../icons/Lock";
import { LeaWorkflowProvider } from "../communication/LeaConversation/LeaWorkflowContext";
import LeaStatus from "../communication/LeaConversation/LeaStatus";
import {
    ClientListReturnState,
    getClientListReturnLocation,
} from "../client_list_navigation";
import Loader from "../../bits/Loader/Loader";

interface ClientStatusViewProps {
    propsClient?: IClient;
}

export default function ClientStatusView({ propsClient }: ClientStatusViewProps) {
    const [notificationStartTime] = React.useState<number>(Date.now());
    const browserLocation = useLocation();
    const history = useHistory();
    const { addToast } = useToasts();

    const parsedQueryParams = queryString.parse(browserLocation.search || "");
    const [activeTab] = React.useState(
        (parsedQueryParams["activeTab"] || "") as ClientStatus
    );

    const [clientUrlId] = React.useState(
        (parsedQueryParams["clientId"] || "") as string
    );

    const [stateClient, setStateClient] = React.useState(
        (browserLocation.state || {}) as ClientListReturnState & {
            client: IClient;
            enableOtherStatuses?: boolean;
        }
    );

    const [pageMarker] = React.useState({
        beforeId: (parsedQueryParams["beforeId"] || "") as string,
        page: (parsedQueryParams["page"] || "") as string,
    });

    const client = stateClient.client;
    const client_id = client?.id || clientUrlId;
    if (!client_id) {
        history.push("/v2/clients");
    }

    // Chart lock functionality
    const { currentLockInfo, subscribeToChartLock, unsubscribeFromChartLock } =
        useChartLock();

    // Check if we're adding a new client
    const isAddingNewClient = parsedQueryParams["client_type"] === "new_client";

    // Only apply chart lock if we're not adding a new client
    const locked =
        !isAddingNewClient &&
        currentLockInfo?.status === ChartLockStatus.LOCKED_BY_OTHER;

    // Subscribe to chart lock changes for this client (only if not adding new client)
    React.useEffect(() => {
        if (client_id && !isAddingNewClient) {
            subscribeToChartLock(client_id);
        }

        return () => {
            if (client_id && !isAddingNewClient) {
                unsubscribeFromChartLock();
            }
        };
    }, [
        client_id,
        subscribeToChartLock,
        unsubscribeFromChartLock,
        isAddingNewClient,
    ]);

    const [, forceUpdate] = React.useState(0);
    const [isRefreshing, setIsRefreshing] = React.useState(false);

    React.useEffect(() => {
        if (!locked) return;

        const interval = setInterval(() => forceUpdate(Date.now()), 120000); // 2 minutes
        return () => clearInterval(interval);
    }, [locked]);

    const triggerRefresh = React.useCallback(async () => {
        if (!client_id) {
            return;
        }
        setIsRefreshing(true);
        try {
            const updatedClient = await store.getClientById(client_id);
            if (!updatedClient) {
                addToast("Unable to reload client.", { appearance: "error" });
                return;
            }
            setStateClient((prevStateClient) => {
                return {
                    ...prevStateClient,
                    client: updatedClient,
                };
            });
        } catch (_error) {
            addToast("Unable to reload client.", { appearance: "error" });
        } finally {
            setIsRefreshing(false);
        }
    }, [addToast, client_id]);

    React.useEffect(() => {
        triggerRefresh();
    }, [triggerRefresh]);

    const aclContext = React.useContext(AclContext);

    const [
        showClientChangeNotificationHistory,
        setShowClientChangeNotificationHistory,
    ] = React.useState(false);
    const [clientChangeNotificationHistory, setClientChangeNotificationHistory] =
        React.useState<RtdClientChangesNotification[]>([]);
    const [dialogOptions, setDialogOptions] = React.useState<{
        notification: RtdClientChangesNotification;
        shouldShow: boolean;
    } | null>(null);
    const [processedNotifications, setProcessedNotifications] = React.useState<
        Set<string>
    >(new Set());
    const dialogOptionsRef = React.useRef(dialogOptions);
    dialogOptionsRef.current = dialogOptions;

    React.useEffect(() => {
        if (!client) {
            return;
        }

        const clientNotificationRef = getClientChangesNotificationRef(client.id);

        const unsubscribe = onChildAdded(clientNotificationRef, (snapshot) => {
            const notification: RtdClientChangesNotification = snapshot.val();

            if (processedNotifications.has(notification.id)) {
                return;
            }

            setClientChangeNotificationHistory((prev) => {
                return uniqBy([...prev, notification], "id");
            });

            setProcessedNotifications((prev) => {
                const newSet = new Set(prev);
                newSet.add(notification.id);
                return newSet;
            });

            // Check if this notification should trigger a dialog
            const isRecentNotification =
                notification.notification_datetime >= notificationStartTime;
            const isFromDifferentUser =
                notification.actioned_by.toLowerCase() !==
                aclContext?.authUser.user?.email?.toLowerCase();
            const isDialogCurrentlyOpen = dialogOptionsRef.current !== null;

            if (
                isRecentNotification &&
                isFromDifferentUser &&
                !isDialogCurrentlyOpen
            ) {
                setDialogOptions({
                    notification: notification,
                    shouldShow: true,
                });
            } else {
                setDialogOptions(null);
            }
        });

        // Cleanup function
        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [
        client?.id,
        aclContext?.authUser.user?.email,
        notificationStartTime,
        client,
        processedNotifications,
    ]);

    React.useEffect(() => {
        dialogOptionsRef.current = dialogOptions;
    }, [dialogOptions]);

    const handleCloseDialog = React.useCallback(() => {
        setDialogOptions(null);
    }, []);

    React.useEffect(() => {
        setProcessedNotifications(new Set());
    }, [client?.id]);

    const showClientChangeHistory = async () => {
        setShowClientChangeNotificationHistory(true);
    };

    const lockBanner = React.useMemo(() => {
        if (
            !locked ||
            !currentLockInfo ||
            !currentLockInfo.lock ||
            isAddingNewClient
        )
            return null;
        const lock = currentLockInfo.lock;
        const lockedBy = lock.locked_by_name;

        return (
            <div className={styles.lockBanner}>
                <div className={styles.lockBannerTitleContainer}>
                    <LockIcon />
                    <span className={styles.lockBannerTitle}>Chart locked:</span>
                </div>

                <span className={styles.lockBannerDescription}>
                    {client.first_name} {client.last_name}'s chart is being edited by{" "}
                    {lockedBy} (
                    {lock.locked_at ? moment(lock.locked_at).fromNow() : "recently"}
                    ). Try again later.
                </span>
            </div>
        );
    }, [
        locked,
        currentLockInfo,
        isAddingNewClient,
        client?.first_name,
        client?.last_name,
    ]);

    return client && client.id ? (
        <LeaWorkflowProvider clientId={client.id}>
            <div className="v2-page">
                <Loader show={isRefreshing} loadingText="Reloading client..." />
                <div>{lockBanner}</div>
                {dialogOptions && (
                    <AlertDialog
                        open={dialogOptions.shouldShow}
                        onClose={handleCloseDialog}
                        onSaveClick={async () => {
                            await triggerRefresh();
                            handleCloseDialog();
                        }}
                        onCancelClick={() => {
                            handleCloseDialog();
                        }}
                        saveText="Refresh"
                        cancelText="Ignore"
                        title="Client data updated"
                        description={
                            <div>
                                <p>
                                    Client data has been updated by{" "}
                                    {dialogOptions.notification.actioned_by} -{" "}
                                    {moment(
                                        dialogOptions.notification
                                            .notification_datetime
                                    ).fromNow()}
                                    . Please refresh the page to see the changes.
                                </p>
                                <p>
                                    Your changes will be lost when you refresh. If
                                    you click "Ignore", please copy your changes and
                                    click on the refresh button.
                                </p>
                            </div>
                        }
                    />
                )}

                {showClientChangeNotificationHistory && (
                    <ClientChangeHistoryNotification
                        key={client.id}
                        clientId={client.id}
                        notifications={clientChangeNotificationHistory}
                        firstName={client.first_name}
                        showPopup={showClientChangeNotificationHistory}
                        onClose={() => setShowClientChangeNotificationHistory(false)}
                    />
                )}
                <div className={styles.headerWrapper}>
                    <div className={styles.headerLeft}>
                        {client.status === ClientStatus.WAITLIST ? (
                            <div>
                                <Button
                                    onClick={() => history.goBack()}
                                    variant="secondary"
                                    size="icon"
                                    title="Back"
                                    aria-label="Back"
                                    className={styles.backButton}
                                >
                                    <ChevronBackIcon />
                                </Button>
                            </div>
                        ) : (
                            <div>
                                <Button
                                    onClick={() => {
                                        const activeTabString = activeTab as string;
                                        const lastStatusTab =
                                            activeTab &&
                                            activeTabString !== "communication" &&
                                            activeTabString !== "tickets" &&
                                            Object.values(ClientStatus).includes(
                                                activeTab as ClientStatus
                                            )
                                                ? activeTab
                                                : stateClient.client.status;

                                        history.push(
                                            getClientListReturnLocation(
                                                stateClient,
                                                {
                                                    pathname: `/v2/clients`,
                                                    search: `?status=${lastStatusTab}&beforeId=${pageMarker.beforeId}&page=${pageMarker.page}`,
                                                }
                                            )
                                        );
                                    }}
                                    variant="secondary"
                                    size="icon"
                                    title="Back"
                                    aria-label="Back"
                                    className={styles.backButton}
                                >
                                    <ChevronBackIcon />
                                </Button>
                            </div>
                        )}

                        {client &&
                        client.email &&
                        client.phone_no &&
                        parsedQueryParams.client_type !== "new_client" ? (
                            <ClientInfo
                                client={client}
                                acl={aclContext?.userAcl}
                                locked={locked}
                                onSaveClient={async (client) => {
                                    const updatedClient = await store.getClientById(
                                        client.id
                                    );
                                    setStateClient((currentStateClient) => ({
                                        ...currentStateClient,
                                        client: updatedClient as IClient,
                                    }));
                                }}
                            />
                        ) : parsedQueryParams.client_type !== "new_client" ? (
                            // ClientInfo (which includes this status) is hidden
                            // without an email or phone, but Lea can still be
                            // handling the inquiry.
                            <LeaStatus client={client} locked={locked} />
                        ) : null}
                    </div>

                    <div className={styles.headerRight}>
                        <Button
                            onClick={triggerRefresh}
                            disabled={isRefreshing}
                            variant="secondary"
                            size="icon"
                            title={
                                isRefreshing ? "Reloading client" : "Reload client"
                            }
                            aria-label={
                                isRefreshing ? "Reloading client" : "Reload client"
                            }
                            aria-busy={isRefreshing}
                        >
                            <RefreshIcon />
                        </Button>
                        <Button
                            variant="secondary"
                            size="icon"
                            title="Change history"
                            aria-label="Change history"
                            onClick={showClientChangeHistory}
                        >
                            <HistoryIcon />
                        </Button>
                    </div>
                </div>
                <EditClient
                    onSaveClient={async (client) => {
                        const updatedClient = await store.getClientById(client.id);
                        if (updatedClient?.status !== ClientStatus.COMPLETED) {
                            setStateClient((currentStateClient) => ({
                                ...currentStateClient,
                                client: updatedClient as IClient,
                            }));
                            const searchParams = new URLSearchParams(
                                browserLocation.search
                            );
                            if (
                                stateClient.client &&
                                propsClient?.id !== updatedClient?.id
                            ) {
                                searchParams.set(
                                    "clientId",
                                    updatedClient?.id as string
                                );
                                searchParams.delete("client_type");
                                history.push({
                                    pathname: browserLocation.pathname,
                                    search: searchParams.toString(),
                                    state: browserLocation.state,
                                });
                            }
                        } else {
                            addToast("Client has been moved to completed.", {
                                appearance: "success",
                                onDismiss: () => {
                                    history.push(
                                        getClientListReturnLocation(stateClient, {
                                            pathname: "/v2/clients",
                                            search: `?status=${client.status}`,
                                        })
                                    );
                                },
                            });
                        }
                    }}
                    clientProp={client}
                    activeTab={activeTab || client.status}
                    locked={locked}
                />
            </div>
        </LeaWorkflowProvider>
    ) : (
        <div></div>
    );
}
